import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from "react-router-dom";

import './OwnerSignUpForm.css'; // Import the CSS file

const geocodeAddress = async (address) => {
  try {
    const response = await axios.get(
      `https://maps.googleapis.com/maps/api/geocode/json?address=${encodeURIComponent(address)}&key=AIzaSyA0uGDU891UTdOlZgObTn5iVSYUOse-zEc`
    );
    if (response.data.results && response.data.results.length > 0) {
      const location = response.data.results[0].geometry.location;
      return location;
    } else {
      console.error('Geocoding failed: No results found for the address.');
      return null;
    }
  } catch (error) {
    console.error('Error during geocoding:', error);
    return null;
  }
};

const OwnerSignUpForm = () => {
  const [inputAddress, setInputAddress] = useState('');
  const [, setLocation] = useState({ lat: 0, lng: 0 });
  const [formData, setFormData] = useState({
    name: '',
    mobile: '',
    email: '',
    password: '',
    confirmPassword: '',
    shopName: '',
    restaurantType: 'restaurant', // Default value
  });

  const [authMethod, setAuthMethod] = useState('email');
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const API_URL = process.env.REACT_APP_API_URL;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  useEffect(() => {
    if (inputRef.current) {
      const autocomplete = new window.google.maps.places.Autocomplete(inputRef.current);
      autocomplete.addListener('place_changed', () => {
        const place = autocomplete.getPlace();
        if (place.geometry) {
          setInputAddress(place.formatted_address);
        }
      });
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (authMethod === 'email' && formData.password !== formData.confirmPassword) {
      alert('Passwords do not match!');
      return;
    }

    try {
      const location = await geocodeAddress(inputAddress);
      if (!location) {
        alert('Could not geocode the address. Please enter a valid address.');
        return;
      }

      setLocation(location);

      const dataToSend = {
        name: formData.name,
        mobile: formData.mobile,
        email: formData.email,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        shopName: formData.shopName,
        restaurantType: formData.restaurantType,
        lat: location.lat,
        lng: location.lng,
      };
      console.log('dataToSend', dataToSend);
      await axios.post(`${API_URL}/api/owner/register`, dataToSend);
      alert('Owner created successfully!');
      window.location.href = '/ownerloginform';
    } catch (error) {
      console.error('Error submitting form:', error);
      alert('There was an error while submitting the form. Please try again.');
    }
  };

  const handleOwnerLogin = () => {
    navigate("/ownerloginform"); // Redirects to the /addCategoryAndItems route
  };


  return (
    <div id="main-container" className="min-h-screen bg-white">
      <header id="header" className="fixed w-full bg-white shadow-sm z-50">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="text-2xl font-bold">Lememeprix</div>
          </div>
        </div>
      </header>

      <main className="pt-20">
        <div id="signup-container" className="relative min-h-[800px] flex items-center justify-center">
          <div className="absolute inset-0 z-0">
            <img
              className="w-full h-full object-cover"
              src="https://storage.googleapis.com/uxpilot-auth.appspot.com/7183815703-988d3526216e8a844f30.png"
              alt="modern french bakery interior with warm lighting and fresh pastries, cinematic style, dark moody atmosphere"
            />
            <div className="absolute inset-0 bg-black bg-opacity-60"></div>
          </div>

          <div id="signup-card" className="relative z-10 bg-white rounded-2xl shadow-2xl w-full max-w-md mx-4">
            <div className="p-8">
              <h2 className="text-3xl font-bold mb-6 text-center">Create Account</h2>

              <form id="signup-form" className="space-y-6" onSubmit={handleSubmit}>
                <div className="space-y-4">
                  {/* Shop Name Input */}
                  <div id="shopName-field" className="relative">
                    <label className="block text-sm font-medium mb-1" htmlFor="shopName">
                      Shop Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="shopName"
                        name="shopName"
                        value={formData.shopName}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black"
                        placeholder="Enter shop name"
                      />
                      <i className="fa-solid fa-check text-green-500 absolute right-3 top-1/2 -translate-y-1/2 hidden"></i>
                    </div>
                  </div>

                  {/* Address Input */}
                  <div id="address-field" className="relative">
                    <label className="block text-sm font-medium mb-1" htmlFor="address">
                      Address
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        ref={inputRef}
                        id="address"
                        placeholder="Enter address"
                        required
                        value={inputAddress}
                        onChange={(e) => setInputAddress(e.target.value)}
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black"
                      />
                      <i className="fa-solid fa-check text-green-500 absolute right-3 top-1/2 -translate-y-1/2 hidden"></i>
                    </div>
                  </div>

                  {/* Restaurant Type Select */}
                  <div id="restaurantType-field" className="relative">
                    <label className="block text-sm font-medium mb-1" htmlFor="restaurantType">
                      Restaurant Type
                    </label>
                    <select
                      name="restaurantType"
                      id="restaurantType"
                      value={formData.restaurantType}
                      onChange={handleInputChange}
                      required
                      className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black"
                    >
                      <option value="restaurant">Restaurant</option>
                      <option value="boulangerie">Boulangerie</option>
                      <option value="cafe">Cafe</option>
                    </select>
                  </div>

                  {/* Name Input */}
                  <div id="name-field" className="relative">
                    <label className="block text-sm font-medium mb-1" htmlFor="name">
                      Full Name
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        id="name"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black"
                        placeholder="Enter your full name"
                      />
                      <i className="fa-solid fa-check text-green-500 absolute right-3 top-1/2 -translate-y-1/2 hidden"></i>
                    </div>
                  </div>

                  {/* Email Input */}
                  <div id="email-field" className="relative">
                    <label className="block text-sm font-medium mb-1" htmlFor="email">
                      Email Address
                    </label>
                    <div className="relative">
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black"
                        placeholder="Enter your email"
                      />
                      <i className="fa-solid fa-check text-green-500 absolute right-3 top-1/2 -translate-y-1/2 hidden"></i>
                    </div>
                  </div>

                  {/* Phone Input */}
                  <div id="phone-field" className="relative">
                    <label className="block text-sm font-medium mb-1" htmlFor="mobile">
                      Phone Number
                    </label>
                    <div className="relative">
                      <input
                        type="tel"
                        id="mobile"
                        name="mobile"
                        value={formData.mobile}
                        onChange={handleInputChange}
                        required
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black"
                        placeholder="+33 1 23 45 67 89"
                      />
                      <i className="fa-solid fa-check text-green-500 absolute right-3 top-1/2 -translate-y-1/2 hidden"></i>
                    </div>
                  </div>

                  {authMethod === 'email' && (
                    <>
                      {/* Password Input */}
                      <div id="password-field" className="relative">
                        <label className="block text-sm font-medium mb-1" htmlFor="password">
                          Password
                        </label>
                        <div className="relative">
                          <input
                            type="password"
                            id="password"
                            name="password"
                            value={formData.password}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black"
                            placeholder="Create a strong password"
                          />
                          <i className="fa-solid fa-eye absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"></i>
                        </div>

                        {/* Password Strength Meter */}
                        <div id="password-strength" className="mt-2">
                          <div className="flex gap-2">
                            <div className="h-1 flex-1 bg-gray-200 rounded-full">
                              <div className="h-full w-1/4 bg-red-500 rounded-full"></div>
                            </div>
                            <div className="h-1 flex-1 bg-gray-200 rounded-full"></div>
                            <div className="h-1 flex-1 bg-gray-200 rounded-full"></div>
                            <div className="h-1 flex-1 bg-gray-200 rounded-full"></div>
                          </div>
                          <p className="text-xs text-gray-500 mt-1">Password strength: Weak</p>
                        </div>
                      </div>

                      {/* Confirm Password Input */}
                      <div id="confirm-password-field" className="relative">
                        <label className="block text-sm font-medium mb-1" htmlFor="confirmPassword">
                          Confirm Password
                        </label>
                        <div className="relative">
                          <input
                            type="password"
                            id="confirmPassword"
                            name="confirmPassword"
                            value={formData.confirmPassword}
                            onChange={handleInputChange}
                            required
                            className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:border-black"
                            placeholder="Confirm your password"
                          />
                          <i className="fa-solid fa-eye absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 cursor-pointer"></i>
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Terms Checkbox */}
                <div id="terms-field" className="flex items-center">
                  <input type="checkbox" className="w-4 h-4 border-gray-300 rounded" />
                  <label className="ml-2 text-sm text-gray-600">
                    I agree to the <span className="text-black font-medium cursor-pointer">Terms of Service</span> and{' '}
                    <span className="text-black font-medium cursor-pointer">Privacy Policy</span>
                  </label>
                </div>

                {/* Submit Button */}
                <button
                  id="signup-button"
                  type="submit"
                  className="w-full bg-red-500 text-white py-4 rounded-lg font-medium hover:bg-red-600 transition-colors"
                >
                  Create Account
                </button>

                {/* Sign In Link */}
                <p className="text-center text-sm">
                  Already have an account?
                  <span             onClick={handleOwnerLogin}
 className="text-black font-medium cursor-pointer hover:underline">Sign In</span>
                </p>
              </form>
            </div>
          </div>
        </div>
      </main>

      <footer id="footer" className="bg-gray-100">
        <div className="container mx-auto px-4 py-12">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h4 className="text-lg font-bold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li>
                  <span className="hover:text-gray-600 cursor-pointer">About Us</span>
                </li>
                <li>
                  <span className="hover:text-gray-600 cursor-pointer">Contact</span>
                </li>
                <li>
                  <span className="hover:text-gray-600 cursor-pointer">Privacy Policy</span>
                </li>
              </ul>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Follow Us</h4>
              <div className="flex space-x-4">
                <span className="text-2xl hover:text-gray-600 cursor-pointer">
                  <i className="fa-brands fa-facebook"></i>
                </span>
                <span className="text-2xl hover:text-gray-600 cursor-pointer">
                  <i className="fa-brands fa-instagram"></i>
                </span>
                <span className="text-2xl hover:text-gray-600 cursor-pointer">
                  <i className="fa-brands fa-twitter"></i>
                </span>
              </div>
            </div>
            <div>
              <h4 className="text-lg font-bold mb-4">Support</h4>
              <p>Need help? Contact us:</p>
              <p className="mt-2">
                <i className="fa-solid fa-phone mr-2"></i>
                +33 1 23 45 67 89
              </p>
              <p className="mt-2">
                <i className="fa-solid fa-envelope mr-2"></i>
                support@lememeprix.com
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OwnerSignUpForm;